import { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import MapboxMap, { Marker, NavigationControl, Popup } from 'react-map-gl'

import plannedSternaPin from 'assets/plannedSternaPin'

import * as S from './style'

type TravelMapProps = {
  itineraries?: any[]
  ownerName?: string
}

type TravelPoint = {
  key: string
  name: string
  latitude: number
  longitude: number
  status: 'visited' | 'inspire'
  itineraryCount: number
  itineraryIds: string[]
}

const normalizeCityName = (name = '') =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

const ProfileTravelMap = ({
  itineraries = [],
  ownerName = 'Viajante'
}: TravelMapProps) => {
  const history = useHistory()
  const mapRef = useRef<any>()
  const [selectedPoint, setSelectedPoint] = useState<TravelPoint | null>(null)
  const [mapReady, setMapReady] = useState(false)
  const [zoom, setZoom] = useState(1.2)

  const points = useMemo<TravelPoint[]>(() => {
    const map = new Map<string, TravelPoint>()

    itineraries.forEach((itinerary) => {
      ;(itinerary?.cities ?? []).forEach((city: any) => {
        const latitude = Number(city?.latitude)
        const longitude = Number(city?.longitude)
        const name = String(city?.name ?? '').trim()

        if (
          !name ||
          !Number.isFinite(latitude) ||
          !Number.isFinite(longitude)
        ) {
          return
        }

        const key = normalizeCityName(name)
        const status: TravelPoint['status'] =
          itinerary?.made && !itinerary?.isEditorial ? 'visited' : 'inspire'
        const existing = map.get(key)

        if (existing) {
          if (itinerary?.id && !existing.itineraryIds.includes(itinerary.id)) {
            existing.itineraryIds.push(itinerary.id)
            existing.itineraryCount = existing.itineraryIds.length
          }

          if (status === 'visited') {
            existing.status = 'visited'
            existing.latitude = latitude
            existing.longitude = longitude
          }

          return
        }

        map.set(key, {
          key,
          name,
          latitude,
          longitude,
          status,
          itineraryCount: 1,
          itineraryIds: itinerary?.id ? [itinerary.id] : []
        })
      })
    })

    return Array.from(map.values()).sort((a, b) => {
      if (a.status !== b.status) return a.status === 'visited' ? -1 : 1
      if (b.itineraryCount !== a.itineraryCount) {
        return b.itineraryCount - a.itineraryCount
      }
      return a.name.localeCompare(b.name, 'pt-BR')
    })
  }, [itineraries])

  const visiblePoints = useMemo(() => {
    if (!mapReady || !mapRef.current) return points

    const accepted: Array<{ point: TravelPoint; x: number; y: number }> = []
    const minimumPixelDistance = zoom >= 8 ? 26 : zoom >= 6 ? 30 : 36

    points.forEach((point) => {
      const projected = mapRef.current.project([
        point.longitude,
        point.latitude
      ])

      const overlaps = accepted.some(({ x, y }) => {
        const dx = projected.x - x
        const dy = projected.y - y
        return Math.sqrt(dx * dx + dy * dy) < minimumPixelDistance
      })

      if (!overlaps) {
        accepted.push({ point, x: projected.x, y: projected.y })
      }
    })

    return accepted.map(({ point }) => point)
  }, [mapReady, points, zoom])

  const visitedCount = points.filter(
    (point) => point.status === 'visited'
  ).length
  const inspireCount = points.filter(
    (point) => point.status === 'inspire'
  ).length

  useEffect(() => {
    if (!mapRef.current || points.length === 0) return

    const bounds = points.reduce(
      (current, point) => {
        current.minLng = Math.min(current.minLng, point.longitude)
        current.maxLng = Math.max(current.maxLng, point.longitude)
        current.minLat = Math.min(current.minLat, point.latitude)
        current.maxLat = Math.max(current.maxLat, point.latitude)
        return current
      },
      {
        minLng: points[0].longitude,
        maxLng: points[0].longitude,
        minLat: points[0].latitude,
        maxLat: points[0].latitude
      }
    )

    if (bounds.minLng === bounds.maxLng && bounds.minLat === bounds.maxLat) {
      mapRef.current.flyTo({
        center: [bounds.minLng, bounds.minLat],
        zoom: 7,
        duration: 0
      })
      return
    }

    mapRef.current.fitBounds(
      [
        [bounds.minLng, bounds.minLat],
        [bounds.maxLng, bounds.maxLat]
      ],
      {
        padding: window.innerWidth < 700 ? 42 : 70,
        maxZoom: 7,
        duration: 0
      }
    )
  }, [points])

  if (points.length === 0) return null

  return (
    <S.Section>
      <S.Header>
        <div>
          <S.Title>Meu mapa de viagens</S.Title>
          <S.Subtitle>
            Viagens realizadas e planejadas de {ownerName}
          </S.Subtitle>
        </div>
        <S.Legend>
          <span>
            <S.LegendPin className="visited">
              <img src="/images/pin.png" alt="" />
            </S.LegendPin>
            Visitadas
          </span>
          <span>
            <S.LegendPin className="inspire">
              <img src={plannedSternaPin} alt="" />
            </S.LegendPin>
            Planejado
          </span>
        </S.Legend>
      </S.Header>

      <S.MapShell>
        <MapboxMap
          ref={mapRef}
          mapboxAccessToken="pk.eyJ1IjoibGF1cmFoZGV2IiwiYSI6ImNsY3BtMTN4dDFueG0zcG81em41YXh4emIifQ.eGKakvFNW6mcmRuHze23mg"
          mapStyle="mapbox://styles/mapbox/streets-v9"
          initialViewState={{ longitude: 0, latitude: 20, zoom: 1.2 }}
          minZoom={1}
          renderWorldCopies={false}
          style={{ width: '100%', height: '100%' }}
          onLoad={() => setMapReady(true)}
          onMove={(event: any) => setZoom(event.viewState.zoom)}
        >
          <NavigationControl position="top-left" showCompass={false} />

          {visiblePoints.map((point) => (
            <Marker
              key={point.key}
              longitude={point.longitude}
              latitude={point.latitude}
              anchor="bottom"
              onClick={(event) => {
                event.originalEvent.stopPropagation()
                setSelectedPoint(point)
              }}
            >
              <S.PinButton
                type="button"
                className={point.status}
                aria-label={`${point.name} - ${
                  point.status === 'visited' ? 'visitada' : 'planejada'
                }`}
              >
                <img
                  src={
                    point.status === 'inspire'
                      ? plannedSternaPin
                      : '/images/pin.png'
                  }
                  alt=""
                />
              </S.PinButton>
            </Marker>
          ))}

          {selectedPoint && (
            <Popup
              longitude={selectedPoint.longitude}
              latitude={selectedPoint.latitude}
              anchor="top"
              closeOnClick={false}
              onClose={() => setSelectedPoint(null)}
              offset={8}
            >
              <S.PopupContent>
                <strong>{selectedPoint.name}</strong>
                <span>
                  {selectedPoint.status === 'visited'
                    ? 'Cidade visitada'
                    : 'Planejada'}
                </span>
                <small>
                  {selectedPoint.itineraryCount}{' '}
                  {selectedPoint.itineraryCount === 1
                    ? 'roteiro relacionado'
                    : 'roteiros relacionados'}
                </small>
                {selectedPoint.itineraryIds[0] && (
                  <button
                    type="button"
                    onClick={() =>
                      history.push(`/roteiro/${selectedPoint.itineraryIds[0]}`)
                    }
                  >
                    Ver roteiro
                  </button>
                )}
              </S.PopupContent>
            </Popup>
          )}
        </MapboxMap>
      </S.MapShell>

      <S.Summary>
        <div>
          <strong>{points.length}</strong>
          <span>destinos no mapa</span>
        </div>
        <div>
          <S.SummaryPin className="visited">
            <img src="/images/pin.png" alt="" />
          </S.SummaryPin>
          <strong>{visitedCount}</strong>
          <span>visitadas</span>
        </div>
        <div>
          <S.SummaryPin className="inspire">
            <img src={plannedSternaPin} alt="" />
          </S.SummaryPin>
          <strong>{inspireCount}</strong>
          <span>planejadas</span>
        </div>
      </S.Summary>
    </S.Section>
  )
}

export default ProfileTravelMap
